// Keeps the current plan's Cherry terms in step with the amount they must be priced for.
//
// Shared by the Builder and Present Mode - the two places a TC can change what a Cherry option
// is financing (a procedure, the per-plan fee, the chairside Pay today). Cherry's plan SET
// depends on the amount, so terms priced for $2,500 are a different offer at $2,400 (see
// cherryTermsMatch). The patient view and the signed PDF read the terms the BACKEND stored, so
// whichever surface changes the amount must re-price it or those go stale.
//
// The host component supplies ONE computed:
//
//   cherryPricing -> cherryPricingFor(options, patientPortion, chosenId, chosenDownpayment)
//                    i.e. { opt, amount } for the enabled Cherry-backed option, or null.
//
// and gets back: cherryTerms, cherryTermsUpdating (feed it to CherryTierList's `updating`),
// cherryAmountToPrice, and refreshCherryTerms() for a manual re-price.
//
// ⛔ Reads the builder slice straight from the store rather than from host props: both hosts
// load it with loadBuilder, and the mixin must not depend on what each names its mapState.
import { mapGetters } from 'vuex';
import { cherryTermsMatch } from '../utils/cherryTerms.js';

// A TC typing a Pay today changes the amount on every keystroke; wait for them to stop.
export const CHERRY_REFETCH_DEBOUNCE_MS = 600;

export default {
  data() {
    return {
      cherryFetchTimer: null,
      // A fetch is scheduled but not started yet - the terms on screen are about to change.
      cherryFetchPending: false,
    };
  },

  computed: {
    ...mapGetters('txPresenter', ['cherryEnabled']),

    cherryBuilderState() {
      return this.$store.state.txPresenter.builder;
    },
    /** Cherry's terms for the current plan: { purchaseAmount, plans, fetchedAt } or null. */
    cherryTerms() {
      return this.cherryBuilderState.cherryTerms || null;
    },
    cherryTermsFetching() {
      return !!this.cherryBuilderState.cherryTermsLoading;
    },
    /**
     * The amount to ask Cherry to price, or null when nothing should be fetched: the site isn't
     * entitled, settings haven't loaded (so we don't know which option is Cherry-backed or the
     * balance layer), no enabled option is Cherry-backed, or there is nothing to finance.
     */
    cherryAmountToPrice() {
      if (!this.cherryEnabled) return null;
      if (!this.$store.state.txPresenter.settings.lastFetchedAt) return null;
      const p = this.cherryPricing;
      return p && p.amount > 0 ? p.amount : null;
    },
    /** True while the figures on screen are about to be (or are being) replaced. */
    cherryTermsUpdating() {
      return this.cherryTermsFetching || this.cherryFetchPending;
    },
  },

  watch: {
    // Fires on plan load (immediate, and again once settings/procedures land) and whenever the
    // priced amount moves. The handler re-checks the stored terms, so a no-op change is free.
    cherryAmountToPrice: {
      immediate: true,
      handler() {
        this.scheduleCherryFetch();
      },
    },
  },

  beforeDestroy() {
    this.clearCherryFetchTimer();
  },

  methods: {
    clearCherryFetchTimer() {
      if (this.cherryFetchTimer) {
        clearTimeout(this.cherryFetchTimer);
        this.cherryFetchTimer = null;
      }
      this.cherryFetchPending = false;
    },

    scheduleCherryFetch() {
      this.clearCherryFetchTimer();
      const amount = this.cherryAmountToPrice;
      if (amount === null || cherryTermsMatch(this.cherryTerms, amount)) return;
      this.cherryFetchPending = true;
      // The first fetch for a plan has nothing on screen to protect - don't make the TC wait.
      // Every later change settles first.
      const delay = this.cherryTerms ? CHERRY_REFETCH_DEBOUNCE_MS : 0;
      this.cherryFetchTimer = setTimeout(() => {
        this.cherryFetchTimer = null;
        this.cherryFetchPending = false;
        this.fetchCherryTermsNow();
      }, delay);
    },

    /** Price the current amount now. Never rejects - a failure leaves the terms null (no figures). */
    fetchCherryTermsNow() {
      const amount = this.cherryAmountToPrice;
      const b = this.cherryBuilderState;
      if (amount === null || !b.siteID || !b.patNum || !b.treatPlanNum) return Promise.resolve(null);
      return this.$store.dispatch('txPresenter/fetchCherryPlanTerms', {
        siteID: b.siteID,
        patNum: b.patNum,
        treatPlanNum: b.treatPlanNum,
        purchaseAmount: amount,
      }).catch(() => null);
    },

    /** Manual "Refresh Cherry terms": re-price even when the stored terms still match. */
    refreshCherryTerms() {
      this.clearCherryFetchTimer();
      return this.fetchCherryTermsNow();
    },
  },
};
